import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import {
  Link,
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'

import LocationSearch from '../../components/location-search'
import Loading from '../../components/loading'
import { ROUTES } from '../../constants/routes'
import useApi from '../../hooks/useApi'
import { charityAddress } from '../../lib/address'
import {
  CHARITY_LOCATION_TYPES,
  charityLocationPayload,
  charityLocationsEqual,
  formatCharityLocationDisplay,
  parseGooglePlaceToCharityLocation,
} from '../../lib/charityLocation'
import {
  charityAccountPath,
  charityPublicProfilePath,
} from '../../lib/charityProfileLink'
import { ensureAbsoluteUrl } from '../../utils/url'
import CharityStripeConnectSection from './dashboard/stripe-connect-section'
import styles from './charity-edit.module.scss'

const EDITABLE_CHARITY_FIELDS = [
  'external_donation_url',
  'amazon_wishlist_url',
  'facebook_link',
  'linkedin_link',
  'instagram_link',
  'youtube_link',
  'tiktok_link',
]

const SOCIAL_FIELDS = [
  {
    id: 'facebook_link',
    label: 'Facebook',
    placeholder: 'https://facebook.com/username',
    iconClass: styles.fbBtn,
  },
  {
    id: 'linkedin_link',
    label: 'LinkedIn',
    placeholder: 'https://linkedin.com/company/username',
    iconClass: styles.liBtn,
  },
  {
    id: 'instagram_link',
    label: 'Instagram',
    placeholder: 'https://instagram.com/username',
    iconClass: styles.igBtn,
  },
  {
    id: 'youtube_link',
    label: 'YouTube',
    placeholder: 'https://youtube.com/@username',
    iconClass: styles.ytBtn,
  },
  {
    id: 'tiktok_link',
    label: 'TikTok',
    placeholder: 'https://tiktok.com/@username',
    iconClass: styles.ttBtn,
  },
]

function buildInitialLocations(charity) {
  return {
    volunteer: charity?.volunteer_location ?? null,
    donation: charity?.donation_location ?? null,
    visitor: charity?.visitor_location ?? null,
  }
}

function ReadOnlyField({ id, label, value }) {
  return (
    <div className={`col-md-6 ${styles.formGroup}`}>
      <div className={styles.roLabel} id={`${id}-label`}>
        {label}
      </div>
      <div className={styles.roValue} aria-labelledby={`${id}-label`}>
        {value || '—'}
      </div>
    </div>
  )
}

function CharityLocationField({ label, value, onChange, placeholder }) {
  return (
    <div className={styles.locationField}>
      <label className={styles.fieldLabel}>{label}</label>
      <LocationSearch
        onChange={onChange}
        placeholder={placeholder}
        value={formatCharityLocationDisplay(value)}
      />
    </div>
  )
}

export default function CharityEdit() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id: idParam } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const charityId = Number(idParam)

  const { getCharity, getUserCharityRegistrations, updateCharity } = useApi()

  const [charity, setCharity] = useState(null)
  const [locations, setLocations] = useState({
    volunteer: null,
    donation: null,
    visitor: null,
  })
  const [initialLocations, setInitialLocations] = useState({
    volunteer: null,
    donation: null,
    visitor: null,
  })
  const [clearedLocations, setClearedLocations] = useState({
    volunteer: false,
    donation: false,
    visitor: false,
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [accessDenied, setAccessDenied] = useState(false)
  const [notFound, setNotFound] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [stripeMessage, setStripeMessage] = useState(null)
  const [verifiedCharities, setVerifiedCharities] = useState([])

  const stripeReturnPath = charityAccountPath(charityId)
  const publicProfilePath = charityPublicProfilePath(charityId)

  const fetchCharity = useCallback(async () => {
    if (!charityId || Number.isNaN(charityId)) {
      setNotFound(true)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    setAccessDenied(false)
    setNotFound(false)

    try {
      const [registrationsResponse, charityResponse] = await Promise.all([
        getUserCharityRegistrations(),
        getCharity(charityId),
      ])

      const registrations = registrationsResponse.data || []
      const verified = registrations.filter(
        reg => reg.verified && reg.status === 'Verified',
      )
      setVerifiedCharities(verified)

      const canEdit = verified.some(
        reg => Number(reg.charity?.id) === charityId,
      )
      if (!canEdit) {
        setAccessDenied(true)
        setLoading(false)
        return
      }

      if (charityResponse.status === 404 || charityResponse.error) {
        setNotFound(true)
        setLoading(false)
        return
      }

      const data = charityResponse.data
      if (!data) {
        setNotFound(true)
        setLoading(false)
        return
      }

      setCharity(data)
      const nextLocations = buildInitialLocations(data)
      setLocations(nextLocations)
      setInitialLocations(nextLocations)
      setClearedLocations({ volunteer: false, donation: false, visitor: false })
      setHasChanges(false)
    } catch (_err) {
      setError(t('charities.settings.save_failed_retry'))
    } finally {
      setLoading(false)
    }
  }, [charityId, getCharity, getUserCharityRegistrations, t])

  useEffect(() => {
    fetchCharity()
  }, [fetchCharity])

  useEffect(() => {
    const stripeReturn = searchParams.get('stripe_return')
    const stripeRefresh = searchParams.get('stripe_refresh')

    if (stripeReturn === 'true') {
      setStripeMessage({
        type: 'success',
        text: t('charities.dashboard.stripe.messages.returnSuccess'),
      })
      searchParams.delete('stripe_return')
      searchParams.delete('charity_id')
      setSearchParams(searchParams, { replace: true })
    } else if (stripeRefresh === 'true') {
      setStripeMessage({
        type: 'warning',
        text: t('charities.dashboard.stripe.messages.sessionExpired'),
      })
      searchParams.delete('stripe_refresh')
      searchParams.delete('charity_id')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams, t])

  const updateCharityField = useCallback((field, value) => {
    setCharity(prev => ({ ...prev, [field]: value }))
    setHasChanges(true)
  }, [])

  const handleLocationChange = useCallback((stateKey, place) => {
    const nextLocation = parseGooglePlaceToCharityLocation(place)
    setLocations(prev => ({ ...prev, [stateKey]: nextLocation }))
    setClearedLocations(prev => ({ ...prev, [stateKey]: nextLocation == null }))
    setHasChanges(true)
  }, [])

  const buildUpdatePayload = useCallback(() => {
    const charityPayload = {}
    EDITABLE_CHARITY_FIELDS.forEach(field => {
      const raw = charity?.[field]
      const normalized =
        field.includes('_url') || field.includes('_link')
          ? ensureAbsoluteUrl(raw) || null
          : raw || null
      charityPayload[field] = normalized
    })

    const payload = { charity: charityPayload }

    CHARITY_LOCATION_TYPES.forEach(({ key, stateKey }) => {
      const current = locations[stateKey]
      const initial = initialLocations[stateKey]

      if (clearedLocations[stateKey] && initial) {
        payload[key] = null
        return
      }

      if (!charityLocationsEqual(current, initial)) {
        if (current) {
          payload[key] = charityLocationPayload(current)
        } else if (initial) {
          payload[key] = null
        }
      }
    })

    return payload
  }, [charity, clearedLocations, initialLocations, locations])

  const onSave = useCallback(async () => {
    if (!charityId || !charity) return

    setSaving(true)
    setError(null)

    try {
      const response = await updateCharity(charityId, buildUpdatePayload())
      const status = response.status ?? response.error?.response?.status

      if (status === 403) {
        setAccessDenied(true)
        setError(t('charities.settings.access_denied'))
        return
      }

      if (response.errors?.length) {
        setError(
          Array.isArray(response.errors)
            ? response.errors.join(', ')
            : String(response.errors),
        )
        return
      }

      if (response.error && status !== 200) {
        setError(
          response.error?.response?.data?.error ||
            t('charities.settings.save_failed'),
        )
        return
      }

      if (response.data) {
        setCharity(response.data)
        const nextLocations = buildInitialLocations(response.data)
        setLocations(nextLocations)
        setInitialLocations(nextLocations)
        setClearedLocations({
          volunteer: false,
          donation: false,
          visitor: false,
        })
      }

      setHasChanges(false)
    } catch (_err) {
      setError(t('charities.settings.save_failed_retry'))
    } finally {
      setSaving(false)
    }
  }, [buildUpdatePayload, charity, charityId, t, updateCharity])

  const irsAddress = useMemo(() => charityAddress(charity), [charity])

  if (Number.isNaN(charityId)) {
    return <Navigate to={ROUTES.CHARITY_ACCOUNT} replace />
  }

  if (loading) {
    return <Loading show />
  }

  if (accessDenied) {
    return (
      <div className="container mt-4">
        <Alert variant="warning">{t('charities.settings.access_denied')}</Alert>
        <Link to="/charities">{t('charities.settings.back_to_charities')}</Link>
      </div>
    )
  }

  if (notFound) {
    return (
      <div className="container mt-4">
        <Alert variant="warning">{t('charities.settings.not_found')}</Alert>
        <Link to="/charities">{t('charities.settings.back_to_charities')}</Link>
      </div>
    )
  }

  if (!charity) {
    return <Loading show />
  }

  return (
    <div className={styles.page}>
      {(loading || saving) && <Loading show />}
      <div className={styles.main}>
        {stripeMessage && (
          <Alert
            variant={stripeMessage.type}
            dismissible
            onClose={() => setStripeMessage(null)}
            className="mb-3"
          >
            {stripeMessage.text}
          </Alert>
        )}
        {error && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setError(null)}
            className="mb-3"
          >
            {error}
          </Alert>
        )}

        <div className={styles.pageHeader}>
          <div className="d-flex align-items-start justify-content-between flex-wrap gap-2">
            <div>
              <h1 className={styles.pageHeaderTitle}>
                {t('charities.settings.title')}
              </h1>
              <p className={styles.orgName}>{charity.organization_name}</p>
              <p className={styles.pageHeaderSubtitle}>
                {t('charities.settings.page_subtitle')}
              </p>
            </div>
            {verifiedCharities.length > 1 ? (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => navigate(ROUTES.CHARITY_ACCOUNT)}
              >
                {t('charities.settings.switch_charity')}
              </Button>
            ) : null}
          </div>
        </div>

        <section className={styles.sectionCard}>
          <div className={styles.sectionCardHeader}>
            <div className={styles.sectionCardHeaderText}>
              <h2>{t('charities.settings.organization_title')}</h2>
              <div className={styles.sectionSubtitle}>
                {t('charities.settings.organization_note')}
              </div>
            </div>
            <span className={styles.badgeVerified}>
              {t('charities.settings.verified_badge')}
            </span>
          </div>
          <div className={styles.sectionCardBody}>
            <div className={styles.irsBlock}>
              <p className={styles.irsBlockText}>
                <strong>{t('charities.settings.irs_block_strong')}</strong>{' '}
                {t('charities.settings.irs_block_text')}
              </p>
            </div>

            {charity.logo_url ? (
              <img
                alt={charity.organization_name}
                className={styles.logoPreview}
                src={charity.logo_url}
              />
            ) : null}

            <div className="row">
              <ReadOnlyField
                id="organization_name"
                label={t('charities.settings.organization_name')}
                value={charity.organization_name}
              />
              <ReadOnlyField
                id="also_known_as"
                label={t('charities.settings.also_known_as')}
                value={charity.also_known_as}
              />
              <ReadOnlyField
                id="ein"
                label={t('charities.settings.ein')}
                value={charity.ein}
              />
              <ReadOnlyField
                id="website_url"
                label={t('charities.settings.website')}
                value={charity.website_url}
              />
              <ReadOnlyField
                id="irs_address"
                label={t('charities.settings.address')}
                value={irsAddress}
              />
            </div>
            <div className={styles.formGroup}>
              <div className={styles.roLabel}>
                {t('charities.settings.mission')}
              </div>
              <div className={styles.roValue}>
                {charity.mission || '—'}
              </div>
            </div>
          </div>
        </section>

        <section className={styles.sectionCard}>
          <div className={styles.sectionCardHeader}>
            <div className={styles.sectionCardHeaderText}>
              <h2>{t('charities.settings.donation_links_title')}</h2>
            </div>
          </div>
          <div className={styles.sectionCardBody}>
            <div className="row">
              <div className={`col-md-6 ${styles.formGroup}`}>
                <label
                  className={styles.fieldLabel}
                  htmlFor="external_donation_url"
                >
                  {t('charities.settings.external_donation_url')}
                </label>
                <input
                  className={styles.fieldInput}
                  id="external_donation_url"
                  type="text"
                  placeholder="https://donate.example.org"
                  value={charity.external_donation_url || ''}
                  onChange={e =>
                    updateCharityField('external_donation_url', e.target.value)
                  }
                />
              </div>
              <div className={`col-md-6 ${styles.formGroup}`}>
                <label
                  className={styles.fieldLabel}
                  htmlFor="amazon_wishlist_url"
                >
                  {t('charities.settings.amazon_wishlist_url')}
                </label>
                <input
                  className={styles.fieldInput}
                  id="amazon_wishlist_url"
                  type="text"
                  placeholder="https://www.amazon.com/hz/wishlist/ls/..."
                  value={charity.amazon_wishlist_url || ''}
                  onChange={e =>
                    updateCharityField('amazon_wishlist_url', e.target.value)
                  }
                />
              </div>
            </div>
          </div>
        </section>

        <section className={styles.sectionCard}>
          <div className={styles.sectionCardHeader}>
            <div className={styles.sectionCardHeaderText}>
              <h2>{t('charities.settings.social_title')}</h2>
            </div>
          </div>
          <div className={styles.sectionCardBody}>
            {SOCIAL_FIELDS.map(field => (
              <div className={styles.formGroup} key={field.id}>
                <label className={styles.fieldLabel} htmlFor={field.id}>
                  {field.label}
                </label>
                <div className={styles.socialFieldRow}>
                  <span
                    className={`${styles.socialIconBtn} ${field.iconClass}`}
                    aria-hidden
                  />
                  <input
                    className={styles.fieldInput}
                    id={field.id}
                    placeholder={field.placeholder}
                    type="text"
                    value={charity[field.id] || ''}
                    onChange={e =>
                      updateCharityField(field.id, e.target.value)
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className={styles.sectionCard}>
          <div className={styles.sectionCardHeader}>
            <div className={styles.sectionCardHeaderText}>
              <h2>{t('charities.settings.locations_title')}</h2>
              <div className={styles.sectionSubtitle}>
                {t('charities.settings.locations_note')}
              </div>
            </div>
          </div>
          <div className={styles.sectionCardBody}>
            <CharityLocationField
              label={t('charities.settings.volunteer_location')}
              onChange={place => handleLocationChange('volunteer', place)}
              placeholder={t('users.edit.search')}
              value={clearedLocations.volunteer ? null : locations.volunteer}
            />
            <CharityLocationField
              label={t('charities.settings.donation_location')}
              onChange={place => handleLocationChange('donation', place)}
              placeholder={t('users.edit.search')}
              value={clearedLocations.donation ? null : locations.donation}
            />
            <CharityLocationField
              label={t('charities.settings.visitor_location')}
              onChange={place => handleLocationChange('visitor', place)}
              placeholder={t('users.edit.search')}
              value={clearedLocations.visitor ? null : locations.visitor}
            />
          </div>
        </section>

        <section className={styles.sectionCard}>
          <div className={styles.sectionCardBody}>
            <CharityStripeConnectSection
              charityId={charityId}
              returnPath={stripeReturnPath}
            />
          </div>
        </section>

        <section className={styles.sectionCard}>
          <div className={styles.saveBar}>
            <div className={styles.saveActions}>
              {publicProfilePath ? (
                <Link
                  className={styles.btnGhost}
                  to={publicProfilePath}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('charities.settings.view_profile')}
                </Link>
              ) : null}
              <button
                className={styles.btnPurple}
                disabled={!hasChanges || saving}
                onClick={onSave}
                type="button"
              >
                {t('charities.settings.save')}
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
